import { useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, uploadFile, openDmsFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DmsFileActions } from "@/components/common/DmsFileActions";
import { SearchInput } from "@/components/common/SearchInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { relative } from "@/lib/format";
import { FileText, Plus } from "lucide-react";
import { UploadProgress } from "@/components/common/UploadProgress";
import { toast } from "sonner";

export default function ProjectDocuments() {
  const { project } = useOutletContext();
  const qc = useQueryClient();
  const { me, can } = useApp();
  const [params] = useSearchParams();
  const preItem = params.get("item");
  const preModule = params.get("module");
  const [adding, setAdding] = useState(!!preItem);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState(preModule || "all");
  const [form, setForm] = useState({ name: "", tags: "", applicable_module_ids: preModule ? [preModule] : [] });
  const [docFile, setDocFile] = useState(null);
  const [docProgress, setDocProgress] = useState(null);

  const { data: docs, isLoading } = useQuery({ queryKey: ["dms-files", "project", project.id], queryFn: () => get(`/pm/projects/${project.id}/documents`) });
  const { data: allItems = [] } = useQuery({ queryKey: ["items", project.id], queryFn: () => get(`/pm/projects/${project.id}/items`) });
  const modules = allItems.filter((i) => i.type === "Module");
  const modMap = useMemo(() => Object.fromEntries(modules.map((m) => [m.id, m])), [modules]);

  const upload = useMutation({
    mutationFn: (fd) => uploadFile("/dms/files", fd, setDocProgress),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dms-files", "project", project.id] }); toast.success("Document uploaded"); setAdding(false); setForm({ name: "", tags: "", applicable_module_ids: [] }); setDocFile(null); setDocProgress(null); },
    onError: (e) => { setDocProgress(null); toast.error(e?.response?.data?.detail || "Could not upload document"); },
  });
  const allTags = [...new Set((docs || []).flatMap((d) => d.tags || []))];
  const filtered = (docs || []).filter((d) =>
    (tagFilter === "all" || (d.tags || []).includes(tagFilter)) &&
    (moduleFilter === "all" || (d.applicable_module_ids || []).includes(moduleFilter)) &&
    (!search || d.name?.toLowerCase().includes(search.toLowerCase())));

  return (
    <div>
      <PageHeader title="Project Documents" subtitle="The data dump — requirements, references, anything worth keeping.">
        <SearchInput testid="doc-search" placeholder="Search name" value={search} onChange={setSearch} />
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-44" data-testid="doc-module-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All modules</SelectItem>{modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-36" data-testid="doc-tag-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All tags</SelectItem>{allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        {can("pm", "create") && <Button data-testid="add-doc-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />Add Document</Button>}
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              {preItem && <p className="mb-2 text-xs text-muted-foreground">Attaching to this ticket's module.</p>}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>File *</Label>
                  <input type="file" data-testid="doc-file" className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground"
                    onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                </div>
                <div className="col-span-2"><Label>Document Name (optional)</Label><Input data-testid="doc-name" className="mt-1" placeholder={docFile?.name} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div><Label>Tags (comma-separated)</Label><Input data-testid="doc-tags" className="mt-1" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} /></div>
                <div>
                  <Label>Applicable Modules</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {modules.map((m) => (
                      <button key={m.id} type="button"
                        onClick={() => setForm((f) => ({ ...f, applicable_module_ids: f.applicable_module_ids.includes(m.id) ? f.applicable_module_ids.filter((x) => x !== m.id) : [...f.applicable_module_ids, m.id] }))}
                        className={`rounded-full border px-2.5 py-1 text-xs ${form.applicable_module_ids.includes(m.id) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                        {m.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <UploadProgress percent={docProgress} />
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)} disabled={upload.isPending}>Cancel</Button>
                <Button size="sm" data-testid="doc-submit" disabled={!docFile || upload.isPending} onClick={() => {
                  const fd = new FormData();
                  fd.append("file", docFile);
                  if (form.name) fd.append("name", form.name);
                  fd.append("tags", form.tags);
                  fd.append("applicable_module_ids", JSON.stringify(form.applicable_module_ids));
                  fd.append("entity_type", preItem ? "work_item" : "project");
                  fd.append("entity_id", preItem || project.id);
                  fd.append("uploaded_by", me?.id || "");
                  upload.mutate(fd);
                }}>Upload</Button>
              </div>
            </div>
          )}
          {!filtered.length ? <EmptyState icon={FileText} title="No documents match" /> : (
            <div className="divide-y divide-border">
              {filtered.map((d) => (
                <div key={d.id} data-testid={`doc-row-${d.id}`} onClick={() => openDmsFile(d.id)} className="group flex cursor-pointer items-center gap-3 px-4 py-3 text-sm hover:bg-muted/50">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{d.name}</div>
                    <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                      {(d.applicable_module_ids || []).map((mid) => modMap[mid] && <StatusBadge key={mid} family="slate" label={modMap[mid].title} />)}
                      {(d.tags || []).map((t) => <span key={t} className="rounded-full bg-muted px-2 py-0.5">{t}</span>)}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{relative(d.created_at)}</span>
                  <DmsFileActions file={d} queryKey={["dms-files", "project", project.id]} canConvert={can("pm", "edit")} canDelete={can("pm", "delete")} />
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
